import { useMutation, UseQueryResult } from '@tanstack/react-query'
import { MissionsFilters, FiltersResourcesEnum } from '../store/filters'
import useStore from '../store'
import {
  fetch,
  create,
  update,
  updateWorkPeriods,
  MissionUpdatePayload,
  Mission,
  MissionCreationPayload,
  MissionWithSummary,
  UserWeekSummary,
  get,
  exportSummary,
  fetchSummary,
  SummaryFilters,
  ExportSummaryParams,
  WeeksSummaryParams,
  fetchWeeksSummary,
  WeeksSummary,
  count,
  unAbandon,
  UnAbandonMissionPayload,
  fetchWeeksSummaryStatus,
  WeeksSummaryStatus,
  UpdateWorkPeriodsPayload,
  UpdateWorkPeriodsResponse,
} from '../api/missions'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import { invalidateWorkPeriodsQueries } from './work-periods'
import { useNormalizeEventsFilters } from '../hooks/use-normalize-events-filters'
import { getWeekdayMondayBased } from '../utils/date'
import { getDay } from 'date-fns'

export const missionKeys = {
  all: ['missions'] as const,
  summary: ['missions/summary'] as const,
  weeksSummary: ['missions/weeks-summary'] as const,
  list: (filters: unknown) => ['missions', filters] as const,
  mutations: {
    create: ['missions', 'create'] as const,
    update: ['missions', 'update'] as const,
    csv: ['missions', 'csv'] as const,
    unAbandon: ['missions', 'unAbandon'] as const,
    updateWorkPeriods: ['missions', 'updateWorkPeriods'] as const,
  },
} as const

//Queries
export const useMissionQuery = (id?: string) => useResource('missions', get, id)

export const useMissionsCountQuery = (
  filters?: MissionsFilters,
  options?: QueryOptions<number>,
): UseQueryResult<number, any> =>
  useResources<number, MissionsFilters>(
    'missions/count',
    count,
    filters || useStore(state => state.filters[FiltersResourcesEnum.missions]),
    options,
  )

export const useMissionsQuery = (
  filters?: Partial<MissionsFilters> & { populate?: string[] },
  options?: QueryOptions<MissionWithSummary[]>,
): UseQueryResult<MissionWithSummary[], any> =>
  useResources<MissionWithSummary[], Partial<MissionsFilters>>(
    'missions',
    fetch as (params: Partial<MissionsFilters>) => Promise<MissionWithSummary[]>,
    filters || useStore(state => state.filters[FiltersResourcesEnum.missions]),
    options,
  )

export const useSummaryQuery = (
  filters?: Partial<SummaryFilters>,
  options?: QueryOptions<UserWeekSummary[]>,
) => {
  const allFilters = filters || useStore(state => state.filters[FiltersResourcesEnum.missions])

  // Extract and exclude summaryMinimalMode and displayDay from filters
  // These are UI-specific and should not be sent to the API
  const { summaryMinimalMode, displayDay, ...params } = allFilters

  // Normalize events filters (transform sentinels to actual event IDs)
  const normalizedEvents = useNormalizeEventsFilters(params?.events)

  // Merge normalized events into params
  const normalizedParams = {
    ...params,
    ...normalizedEvents,
    days: displayDay ? [getWeekdayMondayBased(getDay(new Date(displayDay)))] : undefined,
  }

  // Explicitly reference excluded properties to satisfy linter
  void summaryMinimalMode
  void displayDay

  return useResources<UserWeekSummary[], Partial<SummaryFilters>>(
    'missions/summary',
    fetchSummary,
    normalizedParams,
    options,
  )
}

export const useWeeksSummaryQuery = (
  filters: WeeksSummaryParams,
  options?: QueryOptions<WeeksSummary>,
): UseQueryResult<WeeksSummary, any> =>
  useResources<WeeksSummary, WeeksSummaryParams>(
    'missions/weeks-summary',
    fetchWeeksSummary,
    filters,
    options,
  )

export const useWeeksSummaryStatusQuery = (
  filters: WeeksSummaryParams,
  options?: QueryOptions<WeeksSummaryStatus>,
): UseQueryResult<WeeksSummaryStatus, any> =>
  useResources<WeeksSummaryStatus, WeeksSummaryParams>(
    'missions/weeks-summary/status',
    fetchWeeksSummaryStatus,
    filters,
    options,
  )

//Mutations
export const useCreateMutation = (): UseMutationResult<Mission, MissionCreationPayload> =>
  useMutation(create, {
    mutationKey: missionKeys.mutations.create,
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

export const useUpdateMutation = (): UseMutationResult<Mission, MissionUpdatePayload> =>
  useMutation(update, {
    mutationKey: missionKeys.mutations.update,
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

export const useUpdateWorkPeriodsMutation = (): UseMutationResult<
  UpdateWorkPeriodsResponse,
  any,
  UpdateWorkPeriodsPayload
> =>
  useMutation(updateWorkPeriods, {
    mutationKey: missionKeys.mutations.updateWorkPeriods,
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

/** Mission edit: PUT mission fields then PUT bulk work periods (single loading state). */
export type UpdateMissionWithWorkPeriodsPayload = MissionUpdatePayload & {
  workPeriods: UpdateWorkPeriodsPayload['workPeriods']
}

export const useUpdateMissionWithWorkPeriodsMutation = (): UseMutationResult<
  Mission,
  any,
  UpdateMissionWithWorkPeriodsPayload
> =>
  useMutation(
    async (params: UpdateMissionWithWorkPeriodsPayload) => {
      const { _id, workPeriods, ...rest } = params
      const missionRes = await update({ _id, ...rest })
      await updateWorkPeriods({ missionId: _id, workPeriods })
      return missionRes
    },
    {
      /** Same key as `useUpdateMutation` so UI (e.g. work-period-actions) stays in sync while saving. */
      mutationKey: missionKeys.mutations.update,
      onSuccess: () => {
        invalidateMissionsQueries()
        invalidateWorkPeriodsQueries()
      },
    },
  )

export const useExportSummaryMutation = (): UseMutationResult<string, ExportSummaryParams> =>
  useMutation(exportSummary, {
    mutationKey: missionKeys.mutations.csv,
  })

export const useUnAbandonMutation = (): UseMutationResult<Mission, UnAbandonMissionPayload> =>
  useMutation(unAbandon, {
    mutationKey: missionKeys.mutations.unAbandon,
    onSuccess: () => {
      invalidateMissionsQueries()
      invalidateWorkPeriodsQueries()
    },
  })

//Methods

export const invalidateMissionsQueries = (): void => {
  queryClient.invalidateQueries(missionKeys.all)
  queryClient.invalidateQueries(missionKeys.summary)
  queryClient.invalidateQueries(missionKeys.weeksSummary)
}
